import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import RoutePlaceholder from '../shared/components/RoutePlaceholder'
import ProtectedRoute from '../shared/components/ProtectedRoute'
import AppShell from '../shared/components/AppShell'
import LoginPage from '../modules/register/LoginPage'
import LogoutUsersPage from '../modules/logoutUsers/LogoutUsersPage'
import HistoriesPage from '../modules/histories/HistoriesPage'
import InactivatorPage from '../modules/inactivator/InactivatorPage'
import FeatureTogglePage from '../modules/featuretoggle/FeatureTogglePage'
import FeatureToggleAdminPage from '../modules/featuretoggle/FeatureToggleAdminPage'
import BannerPage from '../modules/banner/BannerPage'
import ClippingsListPage from '../modules/clipping/ClippingsListPage'
import ActionAuditPage from '../modules/actionAudit/ActionAuditPage'
import MigrationListPage from '../modules/migration/MigrationListPage'
import MigrationDetailPage from '../modules/migration/MigrationDetailPage'
import MigrationJsonPage from '../modules/migration/MigrationJsonPage'
import MenemosinePage from '../modules/menemosine/MenemosinePage'
import MenemosineDetailPage from '../modules/menemosine/MenemosineDetailPage'
import CustomersListPage from '../modules/customer/CustomersListPage'
import EditCustomerProfilePage from '../modules/customer/EditCustomerProfilePage'
import JwtLoginPage from '../modules/customer/JwtLoginPage'
import DiscountCouponsPage from '../modules/discountCoupon/DiscountCouponsPage'
import DiscountCouponAddPage from '../modules/discountCoupon/DiscountCouponAddPage'
import AutomaticDiscountCouponAddPage from '../modules/discountCoupon/AutomaticDiscountCouponAddPage'
import AmbassadorDiscountCouponAddPage from '../modules/discountCoupon/AmbassadorDiscountCouponAddPage'
import AutomaticTurboDiscountCouponAddPage from '../modules/discountCoupon/AutomaticTurboDiscountCouponAddPage'
import RobotsPage from '../modules/robots/RobotsPage'
import UsersAdminPage from '../modules/administration/UsersAdminPage'

/**
 * AngularJS (UI-Router) -> React Router path mapping
 * ---------------------------------------------------
 * Extracted from the 23 `$stateProvider.state(...)` calls in `app/main.js`
 * (the only file in the AngularJS app that registers routes — confirmed via
 * `grep -rn "\.state(" app/`). UI-Router nests child state URLs under their
 * parent (`workspace` -> `/workspace`), so the "Legacy URL" column below is
 * the fully resolved path, not just the fragment passed to `.state()`.
 *
 * Legacy state name              | Legacy URL (resolved)              | React Router path
 * --------------------------------|-------------------------------------|--------------------------------------------
 * login                           | /                                   | /login
 * workspace                       | /workspace                          | /workspace (layout/index route)
 * workspace.customers             | /workspace/customers                | /workspace/customers
 * workspace.menemosine            | /workspace/menemosine               | /workspace/menemosine
 * workspace.menemosine-details    | /workspace/menemosine/details       | /workspace/menemosine/details/:id
 * workspace.jwt                   | /workspace/jwt                      | /workspace/jwt
 * workspace.customerProfile       | /workspace/editCustomerProfile/{profile} | /workspace/edit-customer-profile/:profile
 * workspace.settings              | /workspace/settings                 | /workspace/settings
 * workspace.featuretoggle         | /workspace/feature-toggle           | /workspace/feature-toggle
 * workspace.migration             | /workspace/migration                | /workspace/migration
 * workspace.migration-details     | /workspace/migration/{namespace}    | /workspace/migration/:namespace
 * workspace.migration-json        | /workspace/migration/json/{entity}  | /workspace/migration/json/:entity
 * workspace.logoutUsers           | /workspace/logoutUsers              | /workspace/logout-users
 * workspace.discountCoupons       | /workspace/discountCoupon           | /workspace/discount-coupons
 * workspace.discountCouponsAdd    | /workspace/discountCouponsAdd       | /workspace/discount-coupons/add
 * workspace.automaticDiscountCouponsAdd | /workspace/automaticDiscountCouponsAdd | /workspace/discount-coupons/add/automatic
 * workspace.ambassadorDiscountCouponsAdd | /workspace/ambassadorDiscountCouponsAdd | /workspace/discount-coupons/add/ambassador
 * workspace.comboDiscountCouponsAdd | /workspace/comboDiscountCouponsAdd | /workspace/discount-coupons/add/combo
 * workspace.inactivator           | /workspace/inactivator              | /workspace/inactivator
 * workspace.actionAudit           | /workspace/action-audit             | /workspace/action-audit
 * workspace.clippingsList         | /workspace/clippings-list           | /workspace/clippings-list
 * workspace.histories             | /workspace/histories                | /workspace/histories
 * workspace.banner                | /workspace/banner                   | /workspace/banner
 * (n/a - $urlRouterProvider.otherwise) | *                               | * (404 fallback)
 *
 * Notes:
 * - `workspace` itself has no real content of its own in the legacy app
 *   (it's the shell/layout state, `main.html` + `mainController`); it
 *   becomes a layout route rendering `<AppShell />` here (task 010), which
 *   uses React Router's `<Outlet />` internally in place of the legacy
 *   `ui-view` to render the matched child route.
 * - Task 011 (protected routes guard) wraps the `/workspace` layout route
 *   in `ProtectedRoute` (`client/src/shared/components/ProtectedRoute.jsx`),
 *   porting the `data: { requireLogin: true/false }` +
 *   `$stateChangeStart` guard from `app/main.js`. Every legacy state except
 *   `login` has `requireLogin: true`, so guarding the single `/workspace`
 *   parent route covers all 22 of its children without repeating the
 *   check per-route.
 * - `workspace.customerProfile`, `workspace.migration-details` and
 *   `workspace.migration-json` used UI-Router `{param}` syntax; React
 *   Router uses `:param`, so `{profile}` -> `:profile`, `{namespace}` ->
 *   `:namespace`, `{entity}` -> `:entity`.
 * - Route path segments were normalized to kebab-case
 *   (`editCustomerProfile` -> `edit-customer-profile`, `logoutUsers` ->
 *   `logout-users`, `discountCoupon` -> `discount-coupons`) and the four
 *   discount coupon "add" variants were nested under
 *   `/workspace/discount-coupons/add*` since they are all variations of the
 *   same "add coupon" flow. This is a routing-scheme decision only — no
 *   AngularJS behavior is implemented yet, module tasks 031-034 own the
 *   real discount coupon screens and may adjust nesting if needed.
 */

const routeDefs = [
  // `customers` (the main listing/search screen) is intentionally NOT in this
  // placeholder-generating list anymore — task 025 ported it to a real page
  // (`CustomersListPage`), registered explicitly below alongside the other
  // `/workspace` children. Its `edit-customer-profile/:profile` sub-screen
  // was ported by task 026 (see the dedicated comment below); the
  // `editCustomer`/`workspace.customerEdit` navigation target referenced in
  // the legacy controller is dead code (no matching UI-Router state, no
  // live caller) and was never ported — see task 030's review notes
  // (`specs/react-migration/030-module-customer-integration-review.md`).
  // `jwt` is intentionally NOT in this placeholder-generating list anymore
  // — task 027 ported it to a real page (`JwtLoginPage`), registered
  // explicitly below alongside the other `/workspace` children.
  // `edit-customer-profile/:profile` is intentionally NOT in this
  // placeholder-generating list anymore — task 026 ported it to a real page
  // (`EditCustomerProfilePage`), registered explicitly below alongside the
  // other `/workspace` children.
  // `settings` (the "Configurações" screen/tab) was removed entirely — the
  // Astrea environment it configured (`urlAstrea`) is now fixed per deploy
  // via the `ASTREA_ENVIRONMENT` server env var, so there is nothing left
  // for a user to configure here.
  // `feature-toggle` is intentionally NOT in this placeholder-generating
  // list anymore — task 016 ported it to a real page (`FeatureTogglePage`),
  // registered explicitly below alongside the other `/workspace` children.
  // `migration` (the listing screen) is intentionally NOT in this
  // placeholder-generating list anymore — task 021 ported it to a real page
  // (`MigrationListPage`), registered explicitly below alongside the other
  // `/workspace` children. `migration/:namespace` and `migration/json/:entity`
  // (detail/JSON screens) are also NOT here anymore — task 022 ported them
  // to real pages (`MigrationDetailPage`/`MigrationJsonPage`), registered
  // explicitly below alongside the other `/workspace` children.
  // `logout-users` is intentionally NOT in this placeholder-generating list
  // anymore — task 015 ported it to a real page (`LogoutUsersPage`),
  // registered explicitly below alongside the other `/workspace` children.
  // `menemosine` (the listing screen) is intentionally NOT in this
  // placeholder-generating list anymore — task 023 ported it to a real page
  // (`MenemosinePage`), registered explicitly below alongside the other
  // `/workspace` children. `menemosine/details` is also NOT here anymore —
  // task 024 ported it to a real page (`MenemosineDetailPage`), registered
  // explicitly below alongside the other `/workspace` children.
  // `discount-coupons` (the listing screen) is intentionally NOT in this
  // placeholder-generating list anymore — task 031 ported it to a real page
  // (`DiscountCouponsListPage`), registered explicitly below alongside the
  // other `/workspace` children. `discount-coupons/add` (the standard/manual
  // variant) is also NOT here anymore — task 032 ported it to a real page
  // (`DiscountCouponAddPage`), registered explicitly below.
  // `discount-coupons/add/automatic`, `discount-coupons/add/ambassador` and
  // `discount-coupons/add/combo` are also NOT here anymore — task 033 ported
  // them to real pages (`AutomaticDiscountCouponAddPage`/
  // `AmbassadorDiscountCouponAddPage`/`ComboDiscountCouponAddPage`),
  // registered explicitly below.
  // `inactivator` is intentionally NOT in this placeholder-generating list
  // anymore — task 018 ported it to a real page (`InactivatorPage`),
  // registered explicitly below alongside the other `/workspace` children.
  // `action-audit` is intentionally NOT in this placeholder-generating list
  // anymore — task 020 ported it to a real page (`ActionAuditPage`),
  // registered explicitly below alongside the other `/workspace` children.
  // `clippings-list` is intentionally NOT in this placeholder-generating list
  // anymore — task 017 ported it to a real page (`ClippingsListPage`),
  // registered explicitly below alongside the other `/workspace` children.
  // `histories` is intentionally NOT in this placeholder-generating list
  // anymore — task 019 ported it to a real page (`HistoriesPage`), registered
  // explicitly below alongside the other `/workspace` children.
  // `banner` is intentionally NOT in this placeholder-generating list
  // anymore — task 014 ported it to a real page (`BannerPage`), registered
  // explicitly below alongside the other `/workspace` children.
]

function NotFound() {
  return <RoutePlaceholder name="404 - Not Found" />
}

const router = createBrowserRouter([
  {
    // Real page (task 012 — see specs/react-migration/012-login-module.md).
    // Legacy state `login`, URL `/`.
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/workspace',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      // Real page (task 025 — see specs/react-migration/025-module-customer-list.md).
      // Legacy state `workspace.customers`, URL `/workspace/customers`.
      { path: 'customers', element: <CustomersListPage /> },
      // Real page (task 026 — see specs/react-migration/026-module-customer-profile.md).
      // Legacy state `workspace.customerProfile`, URL `/workspace/editCustomerProfile/{profile}`.
      { path: 'edit-customer-profile/:profile', element: <EditCustomerProfilePage /> },
      // Real page (task 027 — see specs/react-migration/027-module-customer-jwt.md).
      // Legacy state `workspace.jwt`, URL `/workspace/jwt`.
      { path: 'jwt', element: <JwtLoginPage /> },
      // Real page (task 015 — see specs/react-migration/015-module-logoutusers.md).
      // Legacy state `workspace.logoutUsers`, URL `/workspace/logoutUsers`.
      { path: 'logout-users', element: <LogoutUsersPage /> },
      // Real page (task 019 — see specs/react-migration/019-module-histories.md).
      // Legacy state `workspace.histories`, URL `/workspace/histories`.
      { path: 'histories', element: <HistoriesPage /> },
      // Real page (task 018 — see specs/react-migration/018-module-inactivator.md).
      // Legacy state `workspace.inactivator`, URL `/workspace/inactivator`.
      { path: 'inactivator', element: <InactivatorPage /> },
      // Real page (task 014 — see specs/react-migration/014-module-banner.md).
      // Legacy state `workspace.banner`, URL `/workspace/banner`.
      { path: 'banner', element: <BannerPage /> },
      // Real page (task 016 — see specs/react-migration/016-module-featuretoggle.md).
      // Legacy state `workspace.featuretoggle`, URL `/workspace/feature-toggle`.
      { path: 'feature-toggle', element: <FeatureTogglePage /> },
      { path: 'feature-toggle/admin', element: <FeatureToggleAdminPage /> },
      // Real page (task 017 — see specs/react-migration/017-module-clipping.md).
      // Legacy state `workspace.clippingsList`, URL `/workspace/clippings-list`.
      { path: 'clippings-list', element: <ClippingsListPage /> },
      // Real page (task 020 — see specs/react-migration/020-module-actionaudit.md).
      // Legacy state `workspace.actionAudit`, URL `/workspace/action-audit`.
      { path: 'action-audit', element: <ActionAuditPage /> },
      // Real page (task 021 — see specs/react-migration/021-module-migration-list.md).
      // Legacy state `workspace.migration`, URL `/workspace/migration`.
      { path: 'migration', element: <MigrationListPage /> },
      // Real page (task 022 — see specs/react-migration/022-module-migration-detail.md).
      // Legacy state `workspace.migration-details`, URL `/workspace/migration/{namespace}`.
      { path: 'migration/:namespace', element: <MigrationDetailPage /> },
      // Real page (task 022 — see specs/react-migration/022-module-migration-detail.md).
      // Legacy state `workspace.migration-json`, URL `/workspace/migration/json/{entity}`.
      { path: 'migration/json/:entity', element: <MigrationJsonPage /> },
      // Real page (task 023 — see specs/react-migration/023-module-menemosine-list.md).
      // Legacy state `workspace.menemosine`, URL `/workspace/menemosine`.
      { path: 'menemosine', element: <MenemosinePage /> },
      // Real page (task 024 — see specs/react-migration/024-module-menemosine-detail.md).
      // Legacy state `workspace.menemosine-details`, URL `/workspace/menemosine/details`.
      { path: 'menemosine/details/:id', element: <MenemosineDetailPage /> },
      // Real page (task 031 — see specs/react-migration/031-module-discountcoupon-list.md).
      // Legacy state `workspace.discountCoupons`, URL `/workspace/discountCoupon`.
      { path: 'discount-coupons', element: <DiscountCouponsPage /> },
      // Real page (task 032 — see specs/react-migration/032-module-discountcoupon-add.md).
      // Legacy state `workspace.discountCouponsAdd`, URL `/workspace/discountCouponsAdd`.
      { path: 'discount-coupons/add', element: <DiscountCouponAddPage /> },
      // Real page (task 033 — see specs/react-migration/033-module-discountcoupon-variants.md).
      // Legacy state `workspace.automaticDiscountCouponsAdd`, URL `/workspace/automaticDiscountCouponsAdd`.
      { path: 'discount-coupons/add/automatic', element: <AutomaticDiscountCouponAddPage /> },
      // Real page (task 033 — see specs/react-migration/033-module-discountcoupon-variants.md).
      // Legacy state `workspace.ambassadorDiscountCouponsAdd`, URL `/workspace/ambassadorDiscountCouponsAdd`.
      { path: 'discount-coupons/add/ambassador', element: <AmbassadorDiscountCouponAddPage /> },
      { path: 'discount-coupons/add/automatic-turbos', element: <AutomaticTurboDiscountCouponAddPage /> },
      { path: 'robots', element: <RobotsPage /> },
      { path: 'administration/users', element: <UsersAdminPage /> },
      ...routeDefs.map(({ path, name, legacyState, legacyUrl }) => ({
        path,
        element: <RoutePlaceholder name={name} legacyState={legacyState} legacyUrl={legacyUrl} />,
      })),
    ],
  },
  // Legacy `login` state's URL was '/', and `$urlRouterProvider.otherwise('/')`
  // sent unmatched routes to it too. Redirect the app root to /login.
  { path: '/', element: <Navigate to="/login" replace /> },
  // Fallback for anything else (equivalent to $urlRouterProvider.otherwise).
  { path: '*', element: <NotFound /> },
])

function AppRoutes() {
  return <RouterProvider router={router} />
}

export default AppRoutes
